import { useState, useEffect } from 'react';
import { TextField } from '@design-system/DataForm/TextField';
import { SelectField } from '@design-system/DataForm/SelectField';
import { RegistrationScreen } from '@/components/utils/RegistrationScreen';
import {
  useCountriesQuery,
  useStatesQuery,
} from '@viome/viome-base/src/usecase/Register/RegisterQuery';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/design-system/Select';

import {
  FormControl,
  FormItem,
  FormLabel,
  FormDescription,
  FormField,
  FormMessage,
} from '@/components/design-system/Form';

import { Link } from '@design-system/Link';
import { Checkbox } from '@/components/design-system/Checkbox';
import { State } from '@viome/viome-base/src/usecase/Register/Register.model';
import useContactInfo from '@/hooks/registration/useContactInfo';
import { linkoutConfigs } from '@/config/routing/LinkoutConfig';
import useRegisterStore, { RegistrationTrack } from '@/hooks/useRegistrationStore';
import * as P from '@/constants/paths';
import { InfoSheet } from '../HealthPages/InfoSheet';
import { INFO_SHEET_CONTENT } from '@/utils/InfoSheet/InfoSheetContent';
import { useUserStore } from '@/hooks/useUserStore';

export const RegistrationContactInformation = () => {
  const { form, onSubmit, isPendingAddressValidation } = useContactInfo();
  const { user: userData } = useUserStore();
  // DE-620: patients (PRO kits) never receive marketing texts — hide the opt-in.
  const proKitOrg = useRegisterStore((state) => state.proKitOrg);

  const [stateOptions, setStateOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const showStateProvince = stateOptions.length > 0;
  const defaultCountry = form.getValues('country') ?? 'US';
  const [selectedCountry, setSelectedCountry] =
    useState<string>(defaultCountry);
  const { data: statesData } = useStatesQuery(selectedCountry);

  const { data, isLoading: isCountryInfoLoading } = useCountriesQuery();
  const previousPath = userData ? P.REGISTRATION_KIT_ID : P.REGISTRATION_PERSONAL_INFORMATION;

  const countryOptions =
    data?.countries
      ?.filter((country) => country.isEnabled)
      ?.map((country) => ({
        value: country.countryCode,
        label: country.name,
      })) || [];

  const phoneCodeOptions =
    data?.countries
      ?.filter((country) => country.isEnabled)
      ?.map((country) => ({
        value: JSON.stringify({
          countryPhoneCode: country.phoneCode,
          countryCode: country.countryCode,
        }),
        label: `${country.phoneCode} (${country.countryCode})`,
      })) || [];

  useEffect(() => {
    if (statesData?.countrySubdivisions) {
      const options = statesData.countrySubdivisions.map((state: State) => ({
        value: state.code,
        label: state.name,
      }));
      setStateOptions(options);

      if (form.getValues('country') !== selectedCountry) {
        form.setValue('state', '');
        form.setValue('phoneCode', '');
      }
    }
  }, [selectedCountry, statesData, form]);

  // clearing the phonecode logic mistake we did in the past for retest users
  useEffect(() => {
    const defaultPhoneCode = form.getValues('phoneCode');
    const defaultCountry = form.getValues('country');
    if (defaultCountry && !defaultPhoneCode) {
      const selectedCountryData = data?.countries.find(
        (country) => country.countryCode === selectedCountry,
      );
      if (selectedCountryData) {
        const phoneCode = JSON.stringify({
          countryPhoneCode: selectedCountryData.phoneCode,
          countryCode: selectedCountryData.countryCode,
        });
        form.setValue('phoneCode', phoneCode);
      }
    }
  }, [selectedCountry, data, form]);

  // DE-620: PRO kits never opt into marketing texts — force off, don't just hide.
  useEffect(() => {
    if (proKitOrg) {
      form.setValue('allowSms', false);
    }
  }, [proKitOrg, form]);

  const onCountryChange = (selectedCountry: string) => {
    setSelectedCountry(selectedCountry);
    form.setValue('country', selectedCountry);

    const selectedCountryData = data?.countries.find(
      (country) => country.countryCode === selectedCountry,
    );

    if (selectedCountryData) {
      const phoneCode = JSON.stringify({
        countryPhoneCode: selectedCountryData.phoneCode,
        countryCode: selectedCountryData.countryCode,
      });
      form.setValue('phoneCode', phoneCode);
    }
  };

  return (
    <RegistrationScreen
      form={form}
      title="Contact Information"
      description="Please enter the information of the person who will be taking the
          test. Learn more here if you have multiple test kits in the same
          household."
      content={
        <div className="mt-8" data-testid="registrationContactInfo-content">
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem data-testid="registrationContactInfo-countryField">
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    onCountryChange(value);
                  }}
                  value={field.value}
                  data-testid="registrationContactInfo-countrySelect"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countryOptions.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <TextField
            name="streetAddress"
            placeholder="Street address"
            className="mt-5"
            data-testid="registrationContactInfo-streetAddressInput"
          />
          <TextField
            name="apartment"
            placeholder="Apartment/Suite No."
            className="mt-5"
            data-testid="registrationContactInfo-apartmentInput"
          />
          <TextField 
            name="city" 
            placeholder="City" 
            className="mt-5" 
            data-testid="registrationContactInfo-cityInput"
          />
          <SelectField
            name="state"
            label="State/Province"
            options={stateOptions}
            className="mt-5"
            data-testid="registrationContactInfo-stateSelect"
          />
          {!showStateProvince && (
            <p className="typography-caption2 text-secondary ml-2 mt-2" data-testid="registrationContactInfo-provinceInfo">
              {`Why can't I select my state or province?`}
              <InfoSheet
                mode='light'
                title={INFO_SHEET_CONTENT.registrationProvince.title}
                description={INFO_SHEET_CONTENT.registrationProvince.description}
              />
            </p>
          )}
          <TextField 
            name="zipCode" 
            placeholder="Zip code" 
            className="mt-5"
            data-testid="registrationContactInfo-zipCodeInput"
          />
          <div className="flex" data-testid="registrationContactInfo-phoneSection">
            <FormField
              control={form.control}
              name="phoneCode"
              render={({ field }) => (
                <FormItem className="mt-5 flex-1" data-testid="registrationContactInfo-phoneCodeField">
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="" />
                    </SelectTrigger>
                    <SelectContent>
                      {phoneCodeOptions.map((phoneCode) => (
                        <SelectItem
                          key={phoneCode.value}
                          value={phoneCode.value}
                        >
                          {phoneCode.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <TextField
              name="phoneNumber"
              placeholder="Phone number"
              className="mt-5 flex-2.7 ml-2"
              maxLength={15}
              numericOnly
              data-testid="registrationContactInfo-phoneNumberInput"
            />
          </div>
          {!proKitOrg && (
          <FormField
            control={form.control}
            name="allowSms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow mt-8" data-testid="registrationContactInfo-smsConsentField">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    data-testid="registrationContactInfo-smsConsentCheckbox"
                  />
                </FormControl>
                <div className="space-y-1 leading-none" data-testid="registrationContactInfo-smsConsentText">
                  <FormLabel>Text me with news and offers</FormLabel>
                  <FormDescription>
                    By checking this box and signing up for texts, you consent
                    to receive marketing text messages (e.g. promos, cart
                    reminders) from Viome at the number provided, including
                    messages sent by autodialer. Consent is not a condition of
                    purchase. Message & data rates may apply. Message frequency varies.
                    Unsubscribe at any time by replying STOP or clicking the
                    unsubscribe link (where available).{' '}
                    <Link to={linkoutConfigs.privacy} target="_blank" data-testid="registrationContactInfo-privacyLink">
                      Privacy Policy
                    </Link>
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
          )}
        </div>
      }
      previousPath={previousPath}
      primaryActionLabel="Next"
      primaryActionIsSubmit
      isLoading={isCountryInfoLoading || isPendingAddressValidation}
      formOnValid={onSubmit}
      checkForRegisterStore={RegistrationTrack.ContactInformation}
      data-testid="registrationContactInfo-screen"
    />
  );
};
